import { getFromHub } from '../actions/networkActions';
import { prepareDateTimeForOverlapCheck, formatTimeHM } from './dateTimeUtils';
import { getTranslation } from './translationUtils';
import { i18n } from '@lingui/core';

export async function checkForOverlaps(fromTimeStr, toTimeStr, options = {}) {
    const {
        currentExcursionId,
        currentRestaurantId,
        preselectedDetailsId,
        selectedAvailFromTime,
        selectedAvailToTime
    } = options;

    const overlaps = [];
    const fromTime = prepareDateTimeForOverlapCheck(fromTimeStr, false);
    const toTime = prepareDateTimeForOverlapCheck(toTimeStr, true);
    const hubOverlaps = await getFromHub(`prebooking/cart/validate?fromTime=${fromTime}&toTime=${toTime}`);

    const extractTacInfo = (overlap) => {
        const partySize = overlap.numberOfAdults + overlap.numberOfChildren;
        const fromTime = overlap.fromDate ? overlap.fromTime : formatTimeHM(overlap.fromTime);
        const toTime = overlap.fromDate ? overlap.toTime : formatTimeHM(overlap.toTime);
        const time = `${fromTime} - ${toTime}`;
        const dateStr = overlap.fromDate ? overlap.fromDate : overlap.fromTime.substring(0, 10);

        return {
            serviceProfileId: overlap.serviceProfile.id,
            title: getTranslation(overlap.serviceProfile.name)?.toUpperCase(),
            passengers: i18n._({
                id: "table_for_n_people",
                message: `Table for {partySize} {partySize, plural, one {person} other {people}}`,
                values: { partySize },
            }),
            time,
            date: dateStr,
        };
    };

    const extractExcursionInfo = (overlap) => {
        const fromTime = formatTimeHM(overlap.fromTime);
        const toTime = overlap.cruiseBookingInformation ? formatTimeHM(overlap.cruiseBookingInformation.toTime) : "";
        const time = toTime ? `${fromTime} - ${toTime}` : fromTime;
        const dateStr = overlap.fromTime ? overlap.fromTime.substring(0, 10) : "";

        return {
            serviceProfileId: overlap.excursion?.id,
            title: getTranslation(overlap.excursion?.name)?.toUpperCase(),
            passengers: overlap.passenger ? `${overlap.passenger.firstName} ${overlap.passenger.lastName}` : "",
            time,
            date: dateStr,
        };
    };

    const parseOverlap = (overlap) => {
        if (overlap.type === "CruiseExcursion") {
            if (currentExcursionId && overlap.excursion?.id === currentExcursionId) {
                return false;
            }
            return extractExcursionInfo(overlap);
        }

        if (overlap.type === "BookingPartTac") {
            return extractTacInfo(overlap);
        }

        if (overlap.type === "Tac") {            
            if (preselectedDetailsId && overlap.id === preselectedDetailsId) {
                return false;
            }

            if (currentRestaurantId && overlap.serviceProfile.id === currentRestaurantId &&
                selectedAvailFromTime === overlap.fromTime &&
                selectedAvailToTime === overlap.toTime) {
                return false;
            }

            return extractTacInfo(overlap);
        }

        return false;
    };

    const parseOverlaps = (potentialOverlaps) => {
        const processedOverlaps = [];

        potentialOverlaps.forEach(item => {
            const overlapToAdd = parseOverlap(item);
            if (overlapToAdd) {
                processedOverlaps.push(overlapToAdd);
            }
        });

        return processedOverlaps;
    };

    const addOverlaps = (parsedOverlaps) => {
        parsedOverlaps.forEach(overlapToAdd => {
            const existing = overlaps.find(o =>
                o.serviceProfileId === overlapToAdd.serviceProfileId &&
                o.title === overlapToAdd.title &&
                o.date === overlapToAdd.date &&
                o.time === overlapToAdd.time
            );
            if (existing) {
                if (overlapToAdd.passengers) {
                    existing.passengers += `, ${overlapToAdd.passengers}`;
                }
            } else {
                overlaps.push(overlapToAdd);
            }
        });
    };

    if (hubOverlaps && hubOverlaps.timeCheckOverlapsBookingParts) {
        Object.keys(hubOverlaps.timeCheckOverlapsBookingParts).forEach((dateTime) => {
            const parsedOverlaps = parseOverlaps(hubOverlaps.timeCheckOverlapsBookingParts[dateTime]);
            if (parsedOverlaps.length) {
                addOverlaps(parsedOverlaps);
            }
        });
    }

    if (hubOverlaps && hubOverlaps.timeCheckOverlapsCartEntries) {
        Object.keys(hubOverlaps.timeCheckOverlapsCartEntries).forEach((dateTime) => {
            const parsedOverlaps = parseOverlaps(hubOverlaps.timeCheckOverlapsCartEntries[dateTime]);
            if (parsedOverlaps.length) {
                addOverlaps(parsedOverlaps);
            }
        });
    }

    return overlaps;
}
